<template>
  <div class="peg-override-form" data-testid="peg-override-form">
    <franchise-select
      elem-id="peg-override-franquia"
      :model-value="franquiaId ?? undefined"
      :label="t('Franquia')"
      data-testid="peg-override-franchise-select"
      @update:model-value="franquiaId = Number($event)"
    />

    <input-text
      v-model="pegUsd"
      elem-id="peg-override-peg"
      :label="t('Peg (USD por crédito)')"
      :placeholder="globalPegPlaceholder"
      data-testid="peg-override-peg-input"
    />

    <p class="peg-hint" data-testid="peg-override-hint">
      {{ hint }}
    </p>

    <input-text
      v-model="reason"
      elem-id="peg-override-reason"
      :label="t('Motivo') + ' (' + t('opcional') + ')'"
      data-testid="peg-override-reason-input"
    />

    <div v-if="errorMessage" class="error-message" data-testid="peg-override-error">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import { useAiSettings } from '../../../composables/useAiSettings'
import { emitValidationFailed } from '../../../composables/useFormValidation'

/**
 * Per-franchise peg override — the burn-rate lever.
 *
 * peg_usd is a STRING here from end to end, deliberately. It is not a
 * presentation choice: JavaScript renders any number below 1e-6 in exponential
 * notation (`String(0.000000001)` === `'1e-9'`), and the backend contract
 * rejects exponential notation by name. Binding this to a numeric input would
 * therefore break every peg small enough to be interesting — which is most of
 * the useful range, since a SMALLER peg is what burns credits faster.
 *
 * The validation below mirrors AiCredits_PegOverrideInput exactly. The server
 * refusal is the authority; this exists so the operator is told before a
 * round-trip, never to replace it.
 */
const { t } = useI18n()
const { savePegOverride, savingPegOverride, peg, loadPeg } = useAiSettings()

const props = defineProps<{ franquiaId?: number | null }>()
const emit = defineEmits<{ saved: [] }>()

/** Matches AiCredits_PegOverrideInput::MAX_DECIMALS. */
const MAX_DECIMALS = 12
/** Matches AiCredits_PegOverrideInput::MIN_PEG_USD. */
const MIN_PEG_USD = 0.000000001

/**
 * The GLOBAL peg, shown as the placeholder: leaving the field empty is not a
 * value, so the operator needs to see what this franchise falls back to before
 * deciding to override it. Loaded here rather than passed in — the modal can be
 * opened from anywhere, and a placeholder that silently goes missing is worse
 * than one that arrives a beat late.
 */
const globalPegPlaceholder = computed<string>(() => {
  const value = peg.value?.ai_credit_peg_usd
  return value ? `${t('Peg global')}: ${value}` : t('Ex.: 0.000040000000')
})

const franquiaId = ref<number | null>(props.franquiaId ?? null)
const pegUsd = ref<string>('')
const reason = ref<string>('')
const errorMessage = ref<string>('')

watch(
  () => props.franquiaId,
  (id) => {
    if (id != null) franquiaId.value = id
  },
)

/**
 * Say what the number MEANS in the operator's units. A raw peg is unreadable —
 * "0.00004" tells nobody how fast anything burns; "a $0.0004 prompt costs 10
 * credits" does.
 */
const hint = computed<string>(() => {
  const peg = Number(pegUsd.value)
  if (!pegUsd.value || Number.isNaN(peg) || peg <= 0) {
    return t('Menor peg = queima mais rápida. Ex.: 0.000040000000')
  }
  const typicalPromptUsd = 0.0004
  const credits = typicalPromptUsd / peg
  const shown = credits >= 100 ? Math.round(credits).toLocaleString('pt-BR') : credits.toFixed(2)
  // Split, not interpolated: t() returns the KEY verbatim until that exact
  // string exists in the loaded messages, and a key returned verbatim is never
  // interpolated — so `{n}` reaches the screen as literal text on any untranslated
  // line. Translating the fragments and concatenating the number keeps the number
  // out of the lookup entirely.
  return `${t('Um prompt típico (US$ 0,0004) consumiria')} ~${shown} ${t('créditos')}.`
})

function validate(): string {
  if (!franquiaId.value || franquiaId.value <= 0) {
    return t('Selecione uma franquia')
  }

  const raw = pegUsd.value.trim()
  if (raw === '') {
    return t('Informe o peg')
  }
  if (/e/i.test(raw)) {
    return t('Use notação decimal simples, não exponencial (0.000000001, não 1e-9)')
  }
  if (Number.isNaN(Number(raw))) {
    return t('O peg deve ser um número decimal')
  }
  if (Number(raw) <= 0) {
    return t('O peg deve ser maior que zero')
  }
  const dot = raw.indexOf('.')
  if (dot !== -1 && raw.length - dot - 1 > MAX_DECIMALS) {
    return `${t('O peg aceita no máximo')} ${MAX_DECIMALS} ${t('casas decimais')}`
  }
  if (Number(raw) < MIN_PEG_USD) {
    return `${t('O peg mínimo é')} ${MIN_PEG_USD}`
  }
  return ''
}

async function handleSave(): Promise<boolean> {
  const problem = validate()
  errorMessage.value = problem
  if (problem !== '') {
    emitValidationFailed('PegOverrideForm', { peg_usd: problem })
    return false
  }

  try {
    await savePegOverride({
      franquia_id: Number(franquiaId.value),
      peg_usd: pegUsd.value.trim(),
      ...(reason.value ? { reason: reason.value } : {}),
    })
    eventBus.emit('globalMessage.success', t('Peg da franquia salvo com sucesso'))
    eventBus.emit('pegOverride.saved')
    emit('saved')
    return true
  } catch (err) {
    log('PegOverrideForm', 'handleSave', 'Erro ao salvar peg override', err)
    const reasonText = err instanceof Error ? err.message : ''
    errorMessage.value = reasonText || t('Erro ao salvar o peg da franquia')
    eventBus.emit('globalMessage.error', errorMessage.value)
    return false
  }
}

onMounted(async () => {
  try {
    await loadPeg()
  } catch (err) {
    log('PegOverrideForm', 'onMounted', 'Peg global indisponível para o placeholder', err)
  }
})

function reset(): void {
  franquiaId.value = props.franquiaId ?? null
  pegUsd.value = ''
  reason.value = ''
  errorMessage.value = ''
}

defineExpose({ handleSave, reset, savingPegOverride })
</script>

<style scoped>
.peg-override-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.peg-hint {
  margin: 0;
  font-size: 0.85rem;
  color: var(--p-text-muted-color, #6b7280);
}

.error-message {
  color: var(--p-red-500, #ef4444);
  font-size: 0.9rem;
}
</style>
